import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  useSequencerStore,
  selectCurrentContainer,
  computeSceneLength,
  type SongScene,
} from '@/stores/sequencerStore'
import type { BankId } from '@busswerks/shared'
import { useSequencerTriggers } from '@/hooks/useSequencerTriggers'
import { FaStop as StopIcon, FaPlay as PlayIcon } from 'react-icons/fa'
import { FaXmark as XIcon } from 'react-icons/fa6'

const BANK_IDS: readonly BankId[] = ['A', 'B', 'C', 'D']
const LONG_PRESS_MS = 320

/**
 * Ableton-session-view song layout. Rows = Scenes, columns = the
 * four fixed Banks (A/B/C/D). Each cell holds one pattern id from
 * its column's bank (or null = silent for that bank in this scene).
 * While a scene is active, all four banks play their assigned
 * patterns simultaneously; each pattern wraps at its own stepCount
 * while the scene clock counts down from `lengthSteps` and hands off
 * to the next scene at the downbeat.
 *
 * Cell tap = assign (opens a picker scoped to THAT column's bank —
 * which eliminates the ghost-pattern bug from the old SongView that
 * iterated the flat global `projects` map).
 *
 * Hold a cell = audition its pattern (one-shot pad-1 preview).
 *
 * Column Stop button = mute that bank in song playback. Tapping a
 * new cell in that column unmutes the bank and assigns the pattern
 * in one gesture (mirrors Ableton's column-stop UX).
 */
export const SongLayout: React.FC = () => {
  const container = useSequencerStore(selectCurrentContainer)
  const projects = useSequencerStore(s => s.projects)
  const currentSceneIndex = useSequencerStore(s => s.currentSceneIndex)
  const bankMuted = useSequencerStore(s => s.bankMuted)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const currentStep = useSequencerStore(s => s.currentStep)

  const addScene = useSequencerStore(s => s.addScene)
  const removeScene = useSequencerStore(s => s.removeScene)
  const renameScene = useSequencerStore(s => s.renameScene)
  const setSceneLengthSteps = useSequencerStore(s => s.setSceneLengthSteps)
  const setSceneCell = useSequencerStore(s => s.setSceneCell)
  const selectScene = useSequencerStore(s => s.selectScene)
  const setBankMuted = useSequencerStore(s => s.setBankMuted)
  const setPlaying = useSequencerStore(s => s.setPlaying)

  const { firePad, currentTime, resume } = useSequencerTriggers()

  const [picker, setPicker] = useState<{
    sceneId: string
    bankId: BankId
  } | null>(null)

  if (!container) {
    return (
      <div className="rounded-xl bg-surface-sunken border border-brd-subtle p-6 text-center text-xs text-content-tertiary italic">
        Loading song…
      </div>
    )
  }

  const scenes = container.scenes
  const sceneIndexById = (id: string) => scenes.findIndex(s => s.id === id)

  return (
    <div className="rounded-xl bg-surface-sunken border border-brd-subtle p-3 flex flex-col min-h-0">
      <div className="flex items-center justify-between mb-2 shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Song · {scenes.length} scene{scenes.length === 1 ? '' : 's'} · tap a
          cell to assign, hold to audition
        </span>
        <button
          type="button"
          onClick={addScene}
          className="px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent transition-colors"
        >
          + Scene
        </button>
      </div>

      {/* Column headers: bank labels + Stop buttons. */}
      <div className="grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_120px] gap-1.5 items-center mb-1 shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Scene
        </span>
        {BANK_IDS.map(bankId => (
          <div
            key={bankId}
            className="flex items-center justify-between gap-1 px-1"
          >
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-secondary">
              {bankId}
            </span>
            <button
              type="button"
              onClick={() => setBankMuted(bankId, !bankMuted[bankId])}
              aria-pressed={bankMuted[bankId]}
              title={
                bankMuted[bankId]
                  ? `Bank ${bankId} is stopped — tap a cell or this button to resume`
                  : `Stop bank ${bankId}`
              }
              className={`grid place-items-center w-5 h-5 rounded text-[9px] transition-colors ${
                bankMuted[bankId]
                  ? 'bg-destructive text-[color:var(--destructive-ink)]'
                  : 'bg-surface-raised/40 text-content-tertiary hover:bg-destructive/20 hover:text-destructive'
              }`}
            >
              <StopIcon size={7} />
            </button>
          </div>
        ))}
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary text-right">
          Bars
        </span>
      </div>

      {scenes.length === 0 ? (
        <div className="py-10 text-center text-xs text-content-tertiary italic">
          No scenes yet. Tap{' '}
          <span className="text-accent font-mono">+ Scene</span> above to add
          one.
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 overflow-y-auto min-h-0">
          {scenes.map((scene, idx) => (
            <SceneRow
              key={scene.id}
              scene={scene}
              sceneIndex={idx}
              isCurrent={idx === currentSceneIndex}
              isPlaying={isPlaying}
              currentStep={currentStep}
              projects={projects}
              onLaunch={() => {
                // Tap play on a scene row → jump to that scene and
                // ensure the transport is running. Previously this
                // only changed `currentSceneIndex`, so the user saw
                // the row highlight but heard nothing because the
                // scheduler wasn't running.
                selectScene(idx)
                if (!isPlaying) {
                  resume()
                  setPlaying(true)
                }
              }}
              onRemove={() => removeScene(scene.id)}
              onRename={name => renameScene(scene.id, name)}
              onSetLength={len => setSceneLengthSteps(scene.id, len)}
              onOpenPicker={bankId => setPicker({ sceneId: scene.id, bankId })}
              onAudition={patternId => {
                const pat = projects[patternId]
                if (!pat) return
                const cell = pat.steps[0]?.[0]
                firePad(0, currentTime(), {
                  velocity: cell?.velocity ?? 1,
                })
              }}
            />
          ))}
        </div>
      )}

      {picker && (
        <PatternPicker
          scene={scenes[sceneIndexById(picker.sceneId)] ?? null}
          bankId={picker.bankId}
          patternIds={container.banks[picker.bankId].patternIds}
          projects={projects}
          onSelect={patternId => {
            setSceneCell(picker.sceneId, picker.bankId, patternId)
            setPicker(null)
          }}
          onClear={() => {
            setSceneCell(picker.sceneId, picker.bankId, null)
            setPicker(null)
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  )
}

// ── Scene row ────────────────────────────────────────────────

interface SceneRowProps {
  scene: SongScene
  sceneIndex: number
  isCurrent: boolean
  isPlaying: boolean
  currentStep: number
  projects: Record<string, { id: string; name: string; stepCount?: number }>
  onLaunch: () => void
  onRemove: () => void
  onRename: (name: string) => void
  onSetLength: (lengthSteps: number) => void
  onOpenPicker: (bankId: BankId) => void
  onAudition: (patternId: string) => void
}

const SceneRow: React.FC<SceneRowProps> = ({
  scene,
  sceneIndex,
  isCurrent,
  isPlaying,
  currentStep,
  projects,
  onLaunch,
  onRemove,
  onRename,
  onSetLength,
  onOpenPicker,
  onAudition,
}) => {
  const [renaming, setRenaming] = useState(false)
  const [draftName, setDraftName] = useState(scene.name)
  const displayName = scene.name.trim() || `Scene ${sceneIndex + 1}`

  // Progress mirrors the scheduler's effective scene length so the
  // bar fills in lockstep with audio — if `scene.lengthSteps` is
  // stale (saved shorter than the longest pattern) the scheduler
  // wraps at the longer effective length, and so does this bar.
  const effectiveLength = computeSceneLength(scene, projects)
  const progress =
    isCurrent && isPlaying && effectiveLength > 0
      ? Math.min(1, (currentStep + 1) / effectiveLength)
      : 0

  // Scene length is stored in 16th-note steps but users think in
  // bars × loops. Show `{bars} × [multiplier]` where `bars` is the
  // longest bank-assigned pattern's measure count (stepCount / 16)
  // and the multiplier is how many times that longest pattern fits
  // inside `lengthSteps`. Editing the multiplier rewrites
  // lengthSteps = longestStepCount × multiplier, which guarantees
  // no pattern gets cut mid-cycle.
  const longestStepCount = Math.max(
    16,
    ...BANK_IDS.map(b => {
      const pid = scene.cells[b]
      const p = pid ? projects[pid] : null
      return p?.stepCount ?? 0
    }),
  )
  const longestBars = Math.max(1, Math.round(longestStepCount / 16))
  const multiplier = Math.max(
    1,
    Math.round(scene.lengthSteps / longestStepCount),
  )

  // Use a local draft string for the multiplier input so the user
  // can clear the field and retype without the controlled-value
  // clamp forcing it back to "1" on every keystroke. Commit on blur
  // or Enter; an empty/invalid draft reverts to the last good value.
  const [multiplierDraft, setMultiplierDraft] = useState(String(multiplier))
  useEffect(() => {
    setMultiplierDraft(String(multiplier))
  }, [multiplier])
  const commitMultiplier = () => {
    const parsed = Math.round(Number(multiplierDraft))
    if (!Number.isFinite(parsed) || parsed < 1) {
      setMultiplierDraft(String(multiplier))
      return
    }
    const clamped = Math.min(64, Math.max(1, parsed))
    onSetLength(clamped * longestStepCount)
  }

  return (
    <div
      className={`relative grid grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_120px] gap-1.5 items-center rounded-md border p-1.5 transition-colors ${
        isCurrent
          ? 'border-[color:var(--accent-halo)] bg-surface-raised/60'
          : 'border-brd-subtle/50 bg-surface-raised/20 hover:border-brd-subtle'
      }`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          type="button"
          onClick={onLaunch}
          title={
            isCurrent
              ? `Scene ${sceneIndex + 1} is playing`
              : `Launch scene ${sceneIndex + 1}`
          }
          className={`grid place-items-center w-5 h-5 rounded transition-colors ${
            isCurrent
              ? 'bg-accent text-[color:var(--accent-ink)]'
              : 'bg-surface-raised/60 text-content-tertiary hover:text-accent'
          }`}
        >
          <PlayIcon size={8} />
        </button>
        {renaming ? (
          <input
            autoFocus
            type="text"
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            onBlur={() => {
              onRename(draftName.trim())
              setRenaming(false)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                ;(e.target as HTMLInputElement).blur()
              } else if (e.key === 'Escape') {
                setDraftName(scene.name)
                setRenaming(false)
              }
            }}
            className="min-w-0 flex-1 bg-surface-base border border-[color:var(--accent-halo)] rounded px-1 text-[10px] font-mono text-content-primary focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onDoubleClick={() => {
              setDraftName(scene.name)
              setRenaming(true)
            }}
            title="Double-click to rename"
            className="min-w-0 flex-1 text-left text-[10px] font-mono text-content-secondary truncate hover:text-content-primary"
          >
            {displayName}
          </button>
        )}
      </div>

      {BANK_IDS.map(bankId => {
        const patternId = scene.cells[bankId]
        const pattern = patternId ? projects[patternId] : null
        return (
          <CellButton
            key={bankId}
            patternName={pattern?.name ?? null}
            onClick={() => onOpenPicker(bankId)}
            onAudition={() => {
              if (patternId) onAudition(patternId)
            }}
          />
        )
      })}

      <div className="flex items-center gap-1">
        <span
          className="text-[10px] font-mono text-content-tertiary tabular-nums"
          title={`Longest pattern in this scene is ${longestBars} bar${longestBars === 1 ? '' : 's'} (${longestStepCount} steps)`}
        >
          {longestBars}×
        </span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={multiplierDraft}
          onChange={e =>
            setMultiplierDraft(e.target.value.replace(/[^\d]/g, ''))
          }
          onBlur={commitMultiplier}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            } else if (e.key === 'Escape') {
              setMultiplierDraft(String(multiplier))
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          title={`Play the longest pattern N times per scene (total ${multiplier * longestStepCount} steps / ${multiplier * longestBars} bar${multiplier * longestBars === 1 ? '' : 's'})`}
          className="w-9 bg-surface-base border border-brd-subtle rounded px-1 text-[10px] font-mono text-content-primary tabular-nums focus:outline-none focus:border-[color:var(--accent-halo)]"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Delete ${displayName}`}
          title={`Delete ${displayName}`}
          className="grid place-items-center w-6 h-6 rounded border border-brd-subtle text-content-tertiary hover:text-destructive hover:border-destructive hover:bg-destructive/10 transition-colors"
        >
          <XIcon size={11} />
        </button>
      </div>

      {isCurrent && isPlaying && (
        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-surface-raised/40 overflow-hidden rounded-b-md"
        >
          <div
            className="h-full bg-accent transition-[width] duration-75 ease-linear"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}
    </div>
  )
}

// ── Cell ─────────────────────────────────────────────────────

interface CellButtonProps {
  patternName: string | null
  onClick: () => void
  onAudition: () => void
}

const CellButton: React.FC<CellButtonProps> = ({
  patternName,
  onClick,
  onAudition,
}) => {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const auditionFired = useRef(false)

  const cancelHold = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handlePointerDown = () => {
    auditionFired.current = false
    longPressTimer.current = setTimeout(() => {
      auditionFired.current = true
      onAudition()
    }, LONG_PRESS_MS)
  }

  const handleClick = () => {
    if (auditionFired.current) {
      auditionFired.current = false
      return
    }
    onClick()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      title={
        patternName
          ? `${patternName} · tap to change, hold to audition`
          : 'Empty cell · tap to assign a pattern, hold to audition'
      }
      className={`h-8 min-w-0 rounded-md border px-1.5 text-[10px] font-mono truncate transition-colors ${
        patternName
          ? 'bg-accent/20 border-accent/50 text-content-primary hover:bg-accent/30 hover:border-accent'
          : 'bg-surface-raised/30 border-dashed border-brd-subtle/60 text-content-tertiary/70 hover:border-brd-subtle hover:text-content-secondary'
      }`}
    >
      {patternName ?? '—'}
    </button>
  )
}

// ── Pattern picker ───────────────────────────────────────────

interface PatternPickerProps {
  scene: SongScene | null
  bankId: BankId
  patternIds: string[]
  projects: Record<string, { id: string; name: string }>
  onSelect: (patternId: string) => void
  onClear: () => void
  onClose: () => void
}

const PatternPicker: React.FC<PatternPickerProps> = ({
  scene,
  bankId,
  patternIds,
  projects,
  onSelect,
  onClear,
  onClose,
}) => {
  const patterns = useMemo(
    () =>
      patternIds
        .map(pid => projects[pid])
        .filter((p): p is NonNullable<typeof p> => !!p),
    [patternIds, projects],
  )

  if (!scene) {
    onClose()
    return null
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-label={`Choose a pattern for bank ${bankId}`}
      onClick={onClose}
    >
      <div
        className="max-w-md w-full rounded-xl bg-surface-raised border border-brd-subtle shadow-high p-4 flex flex-col gap-3"
        onClick={e => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <div className="text-xs font-mono text-content-secondary">
            Bank {bankId} · pick a pattern
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-content-tertiary hover:text-content-primary"
          >
            <XIcon size={14} />
          </button>
        </header>
        <div className="flex flex-col gap-1 max-h-[60vh] overflow-y-auto">
          {patterns.length === 0 ? (
            <p className="text-xs text-content-tertiary italic py-2">
              Bank {bankId} has no patterns yet. Add one in the step editor
              first.
            </p>
          ) : (
            patterns.map(p => {
              const active = scene.cells[bankId] === p.id
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelect(p.id)}
                  className={`text-left px-3 py-2 rounded-md text-xs font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)]'
                      : 'bg-surface-sunken text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-content-primary'
                  }`}
                >
                  {p.name}
                </button>
              )
            })
          )}
        </div>
        <footer className="flex items-center justify-between pt-1 border-t border-brd-subtle">
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] font-mono text-destructive hover:brightness-125"
          >
            Clear cell
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[10px] font-mono text-content-tertiary hover:text-content-secondary"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  )
}
